import React, { useEffect, useMemo, useState } from 'react';

import useAsyncActionStatus from '~/hooks/useAsyncActionStatus';
import { getContractDetails, getDetails } from '~/store/contracts/actions';
import { ContractInfo } from '~/typings/entities/ContractInfo';
import { ModalsProvider } from '~/contexts/ModalsContext';
import { CONTRACT_FIELDS_MODALS } from '~/screens/ContractDetails/modals';
import { DrawerProvider } from '~/hooks/useDrawer';
import { hasAuxContractId } from '~/components/Ticket/common/ticketContracts';

import { ContractAdditionalInfoView } from './views/ContractAdditionalInfoView';
import { ContractCommentsView } from './views/ContractCommentsView';
import { ContractFilesView } from './views/ContractFilesView';
import { ContractInstallmentsView } from './views/ContractInstallmentsView';
import { ContractMoreDetails } from './views/ContractMoreDetails';
import { ContractOverview } from './views/ContractOverview';
import { ContractPendenciesView } from './views/ContractPendenciesView';
import { ContractHistoryView } from './views/ContractHistoryView';
import { ContractRefinancedView } from './views/ContractRefinancedView';
import { ContractReturnsView } from './views/ContractReturnsView';
import { CardOtherInfoView } from './views/CardOtherInfoView';
import { CardFilesView } from './views/CardFilesView';
import { CardHistoryView } from './views/CardHistoryView';
import { CardPendenciesView } from './views/CardPendenciesView';
import { CardReturnsView } from './views/CardReturnsView';
import { Wrapper } from './ContractTab.styled';

export const CARD_LOAN_TYPES = ['PIX_CREDIT_CARD', 'CONSIGNED_CARD_RMC', 'BENEFIT_CARD_RCC'] as const;

export type ContractView =
  | 'overview'
  | 'details'
  | 'returns'
  | 'files'
  | 'pendencies'
  | 'installments'
  | 'comments'
  | 'history'
  | 'ported'
  | 'refinanced'
  | 'additionalInfo'
  | 'card-other-info'
  | 'card-files'
  | 'card-history'
  | 'card-pendencies'
  | 'card-returns';

function normalizeContractDetails(result: any, hasAuxId: boolean) {
  if (!result || !hasAuxId) return result;
  const inner = result.contract;
  const info = result.contractInfo;
  return {
    ...result,
    netValue: inner?.value,
    numberOfInstallments: info?.installments,
    contractDate: inner?.contractDate,
    consigneeReturnDate: info?.paymentDate,
    operationStep: inner?.operationStep?.name,
    loanType: inner?.product,
  };
}

export type ContractTabTicketContract = {
  id: string;
  contractId?: number | string | null;
};

export type ContractTabProps = {
  contractIds: string[];
  contracts?: ContractTabTicketContract[];
};

function ContractTabContent({ contractIds, contracts = [] }: ContractTabProps) {
  const [view, setView] = useState<ContractView>('overview');
  const [contractId, setContractId] = useState<string>(String(contractIds?.[0] ?? ''));

  const currentContract = contracts.find((c) => c.id === contractId);
  const hasAuxId = hasAuxContractId(currentContract);
  const backofficeContractId = hasAuxId ? currentContract?.contractId : undefined;

  // Both hooks called unconditionally — selected based on hasAuxId below
  const telliControl = useAsyncActionStatus(getContractDetails, contractId);
  const backofficeControl = useAsyncActionStatus(getDetails, contractId);

  const detailsControl = hasAuxId ? backofficeControl : telliControl;

  const contractAuxIds = useMemo(
    () =>
      contracts.reduce((acc: Record<string, number>, c) => {
        if (hasAuxContractId(c)) acc[c.id] = Number(c.contractId);
        return acc;
      }, {}),
    [contracts],
  );

  const contractInfo = useMemo(
    () => (detailsControl.result?.id ? new ContractInfo(detailsControl.result) : null),
    [detailsControl.result],
  );

  const handleNavigate = (nextView: ContractView) => setView(nextView);
  const handleBack = () => setView('overview');
  const handleContractChange = (id: string) => {
    setContractId(id);
    setView('overview');
  };

  // detailsControl.loading is unreliable for first-time actionId fetches due to a library bug:
  // when START is dispatched for a new actionId, stored[1] === NEVER causes getData to return
  // state: stored?.state (undefined on arrays) instead of stored[0], so isRunning() returns false.
  // detailsControl.success reads state directly from Redux ('SUCCESS') and is always reliable.
  const loading = detailsControl.loading || (!detailsControl.failure && !detailsControl.success);

  const sharedCardProps = {
    contractId,
    contractIds: contractIds.map(String),
    contractAuxIds,
    loading,
    contractDetails: normalizeContractDetails(detailsControl.result, hasAuxId),
    hasAuxId,
    onContractChange: handleContractChange,
  };

  useEffect(() => {
    if (!contractId) return;
    if (hasAuxId) {
      backofficeControl.start({ id: currentContract.contractId }, { actionId: contractId });
    } else {
      telliControl.start({ id: contractId }, { actionId: contractId });
    }
  }, [contractId, hasAuxId]);

  useEffect(
    () => () => {
      telliControl.clear();
      backofficeControl.clear();
    },
    [],
  );

  if (!contractId) return null;

  return (
    <Wrapper>
      {view === 'overview' && (
        <div data-testid="contract-tab-overview">
          <ContractOverview {...sharedCardProps} onNavigate={handleNavigate} />
        </div>
      )}
      {view === 'details' && <ContractMoreDetails {...sharedCardProps} onBack={handleBack} />}
      {view === 'installments' && <ContractInstallmentsView contractId={contractId} onBack={handleBack} />}
      {view === 'returns' && (
        <ContractReturnsView {...sharedCardProps} contractInfo={contractInfo} onBack={handleBack} />
      )}
      {view === 'files' && <ContractFilesView {...sharedCardProps} onBack={handleBack} />}
      {view === 'additionalInfo' && <ContractAdditionalInfoView {...sharedCardProps} onBack={handleBack} />}
      {view === 'comments' && <ContractCommentsView {...sharedCardProps} onBack={handleBack} />}
      {view === 'pendencies' && <ContractPendenciesView contractId={contractId} onBack={handleBack} />}
      {view === 'history' && (
        <ContractHistoryView contractId={contractId} contractDetails={detailsControl.result} onBack={handleBack} />
      )}
      {(view === 'ported' || view === 'refinanced') && (
        <ContractRefinancedView contractId={contractId} onBack={handleBack} />
      )}
      {view === 'card-other-info' && (
        <CardOtherInfoView contractId={contractId} contract={detailsControl.result} onBack={handleBack} />
      )}
      {view === 'card-files' && (
        <CardFilesView contractId={contractId} backofficeContractId={backofficeContractId} onBack={handleBack} />
      )}
      {view === 'card-history' && (
        <CardHistoryView contractId={contractId} contract={detailsControl.result} onBack={handleBack} />
      )}
      {view === 'card-pendencies' && (
        <CardPendenciesView contractId={contractId} backofficeContractId={backofficeContractId} onBack={handleBack} />
      )}
      {view === 'card-returns' && (
        <CardReturnsView contractId={contractId} contract={detailsControl.result} onBack={handleBack} />
      )}
    </Wrapper>
  );
}

export function ContractTab({ contractIds, contracts }: ContractTabProps) {
  return (
    <DrawerProvider>
      <ModalsProvider>
        <ContractTabContent contractIds={contractIds} contracts={contracts} />
        {CONTRACT_FIELDS_MODALS.map((Modal) => (
          <Modal key={Modal.alias} />
        ))}
      </ModalsProvider>
    </DrawerProvider>
  );
}
